import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Search, RefreshCw, Save, Lock, Unlock,
    Users, ChevronRight, Shield, X,
    Database, AlertCircle
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

// ─── Helpers ──────────────────────────────────────────────────────────────────
// role_Position format: { "userId": 132, "referenceCards": [14, 15] }
const parseRolePosition = (raw) => {
    try {
        if (typeof raw === "string" && raw.startsWith("{")) return JSON.parse(raw);
    } catch { }
    return { userId: null, referenceCards: [] };
};

const AVATAR_COLORS = [
    { bg: "#DBEAFE", text: "#1D4ED8" },
    { bg: "#E0F2FE", text: "#0369A1" },
    { bg: "#DCFCE7", text: "#15803D" },
    { bg: "#FEF3C7", text: "#B45309" },
    { bg: "#EDE9FE", text: "#6D28D9" },
    { bg: "#FCE7F3", text: "#BE185D" },
    { bg: "#CCFBF1", text: "#0F766E" },
    { bg: "#FEE2E2", text: "#B91C1C" },
];

const getAvatarColor = (str) => {
    let hash = 0;
    for (let i = 0; i < (str || "").length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (firstName, lastName) =>
    `${(firstName || "?")[0]}${(lastName || "?")[0]}`.toUpperCase();

// ─── Profile cache ─────────────────────────────────────────────────────────────
const profileCache = {};

const resolveProfileSrc = async (res) => {
    const contentType = res.headers.get("content-type") || "";
    if (contentType.startsWith("image/")) {
        const blob = await res.blob();
        if (blob.size === 0) throw new Error("empty blob");
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
    if (contentType.includes("application/json")) {
        const json = await res.json();
        const raw = typeof json === "string"
            ? json
            : json?.profile ?? json?.Profile ?? json?.image ?? json?.data ?? null;
        if (!raw) throw new Error("no data in json");
        if (raw.startsWith("data:")) return raw;
        const mime = raw.startsWith("/9j") ? "image/jpeg" : "image/png";
        return `data:${mime};base64,${raw}`;
    }
    const text = await res.text();
    if (!text || text.trim() === "" || text === "null") throw new Error("empty text");
    if (text.startsWith("data:")) return text;
    const mime = text.trim().startsWith("/9j") ? "image/jpeg" : "image/png";
    return `data:${mime};base64,${text.trim()}`;
};

// ─── Avatar ────────────────────────────────────────────────────────────────────
function UserAvatar({ user, size = 36, radius = 9, fontSize = 12 }) {
    const username = user?.username ?? user?.userName ?? user?.Username ?? null;
    const { bg, text } = getAvatarColor(user?.id?.toString() || user?.firstName || "");
    const initials = getInitials(user?.firstName, user?.lastName);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!username) return;
        if (profileCache[username]) return;
        profileCache[username] = { src: null, status: "loading" };
        fetch(`${API_BASE_URL}api/Users/username/${encodeURIComponent(username)}/profile`)
            .then(async (res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const src = await resolveProfileSrc(res);
                profileCache[username] = { src, status: "ok" };
            })
            .catch(() => { profileCache[username] = { src: null, status: "none" }; })
            .finally(() => setTick(t => t + 1));
    }, [username]);

    const cached = username ? profileCache[username] : null;
    const src = cached?.src ?? null;

    const baseStyle = {
        width: size, height: size, borderRadius: radius, flexShrink: 0,
        overflow: "hidden", display: "flex", alignItems: "center",
        justifyContent: "center", background: bg,
    };

    if (src) return (
        <div style={baseStyle}>
            <img src={src} alt={initials}
                onError={() => { profileCache[username] = { src: null, status: "none" }; setTick(t => t + 1); }}
                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
    );

    if (cached?.status === "loading") return (
        <div style={{ ...baseStyle, background: "#E2E8F0", position: "relative", overflow: "hidden" }}>
            <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.6) 50%, transparent 75%)",
                animation: "shimmer 1.2s infinite",
            }} />
            <style>{`@keyframes shimmer { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }`}</style>
        </div>
    );

    return <div style={{ ...baseStyle, color: text, fontSize, fontWeight: 700 }}>{initials}</div>;
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function ReferenceCardAccess() {
    const [users, setUsers] = useState([]);
    const [referenceCards, setReferenceCards] = useState([]);
    const [savedAccess, setSavedAccess] = useState([]);
    const [userLevelMap, setUserLevelMap] = useState({});

    const [selectedUser, setSelectedUser] = useState(null);
    // permissions keyed by String(userId): { [cardId]: true|false }
    const [permissions, setPermissions] = useState({});
    const [origPermissions, setOrigPermissions] = useState({});
    const [isDirty, setIsDirty] = useState(false);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [userSearch, setUserSearch] = useState("");
    const [cardSearch, setCardSearch] = useState("");

    const selectedUserRef = useRef(null);
    useEffect(() => { selectedUserRef.current = selectedUser; }, [selectedUser]);

    // ── fetchData ───────────────────────────────────────────────────────────────
    const fetchData = async () => {
        try {
            setLoading(true);
            const [usersRes, levelsRes, cardsRes, accessRes] = await Promise.all([
                fetch(`${API_BASE_URL}api/Users`),
                fetch(`${API_BASE_URL}api/UserLevels`),
                fetch(`${API_BASE_URL}api/ReferenceCards`),
                fetch(`${API_BASE_URL}api/Ref_Access_cards`),
            ]);

            if (!usersRes.ok) throw new Error("Failed to fetch users");
            if (!levelsRes.ok) throw new Error("Failed to fetch user levels");
            if (!cardsRes.ok) throw new Error("Failed to fetch reference cards");

            const [usersData, levelsData, cardsData] = await Promise.all([
                usersRes.json(), levelsRes.json(), cardsRes.json()
            ]);

            const lvlMap = {};
            levelsData.forEach(l => { lvlMap[l.id] = l; });
            setUserLevelMap(lvlMap);

            const activeUsers = usersData.filter(u => u.isActive === 1 || u.isActive === true);

            activeUsers.forEach((user) => {
                const username = user?.username ?? user?.userName ?? user?.Username ?? null;
                if (!username || profileCache[username]) return;
                profileCache[username] = { src: null, status: "loading" };
                fetch(`${API_BASE_URL}api/Users/username/${encodeURIComponent(username)}/profile`)
                    .then(async (res) => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const src = await resolveProfileSrc(res);
                        profileCache[username] = { src, status: "ok" };
                    })
                    .catch(() => { profileCache[username] = { src: null, status: "none" }; });
            });

            setUsers(activeUsers);
            setReferenceCards(cardsData.filter(c => c.isActive === 1));

            // Re-sync selectedUser
            const currentSelected = selectedUserRef.current;
            if (currentSelected) {
                const fresh = activeUsers.find(u => u.id === currentSelected.id);
                setSelectedUser(fresh || null);
            }

            // ── Build permissions keyed by userId ─────────────────────────────
            let permObj = {};
            if (accessRes.ok) {
                const accessData = await accessRes.json();
                setSavedAccess(accessData);
                accessData.forEach((a) => {
                    if (!a.isActive) return;
                    const parsed = parseRolePosition(a.role_Position);
                    if (parsed.userId == null || !Array.isArray(parsed.referenceCards)) return;
                    const key = String(parsed.userId);
                    if (!permObj[key]) permObj[key] = {};
                    parsed.referenceCards.forEach(id => { permObj[key][id] = true; });
                });
            } else {
                setSavedAccess([]);
            }

            setPermissions(permObj);

            // Sync origPermissions for currently selected user
            if (currentSelected) {
                const key = String(currentSelected.id);
                setOrigPermissions(prev => ({ ...prev, [key]: { ...(permObj[key] || {}) } }));
            }

            return permObj;
        } catch (err) {
            Swal.fire({ icon: "error", title: "Error", text: err.message });
            return null;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const selectedUserLevel = selectedUser ? userLevelMap[selectedUser.userLevelId] || null : null;
    const selectedKey = selectedUser ? String(selectedUser.id) : null;

    const filteredUsers = useMemo(() => {
        const q = userSearch.toLowerCase();
        return users.filter(u => {
            const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
            const role = (userLevelMap[u.userLevelId]?.userLevelName || "").toLowerCase();
            return !q || fullName.includes(q) || role.includes(q) || u.email?.toLowerCase().includes(q);
        });
    }, [users, userSearch, userLevelMap]);

    const filteredCards = useMemo(() => {
        const q = cardSearch.toLowerCase();
        return !q ? referenceCards : referenceCards.filter(c =>
            c.title.toLowerCase().includes(q) || c.pageKey.toLowerCase().includes(q)
        );
    }, [referenceCards, cardSearch]);

    const toggleCard = (cardId) => {
        if (!selectedKey) return;
        setPermissions(prev => ({
            ...prev,
            [selectedKey]: { ...prev[selectedKey], [cardId]: !prev[selectedKey]?.[cardId] }
        }));
        setIsDirty(true);
    };

    const selectAll = (val) => {
        if (!selectedKey) return;
        const newPerms = {};
        referenceCards.forEach(c => { newPerms[c.id] = val; });
        setPermissions(prev => ({ ...prev, [selectedKey]: newPerms }));
        setIsDirty(true);
    };

    const enabledCount = selectedKey
        ? Object.values(permissions[selectedKey] || {}).filter(Boolean).length
        : 0;

    const handleSelectUser = (user) => {
        setPermissions(prev => {
            if (isDirty && selectedKey) {
                const reverted = { ...prev, [selectedKey]: { ...(origPermissions[selectedKey] || {}) } };
                const newKey = String(user.id);
                setOrigPermissions(p => ({ ...p, [newKey]: { ...(reverted[newKey] || {}) } }));
                return reverted;
            }
            const newKey = String(user.id);
            setOrigPermissions(p => ({ ...p, [newKey]: { ...(prev[newKey] || {}) } }));
            return prev;
        });
        setIsDirty(false);
        setSelectedUser(user);
        setCardSearch("");
    };

    const handleSave = async () => {
        if (!selectedUser) return;

        const userId = selectedUser.id;
        const userKey = String(userId);
        const userName = `${selectedUser.firstName} ${selectedUser.lastName}`;

        const result = await Swal.fire({
            title: "Save Access?",
            html: `Saving card access for <strong>${userName}</strong><br/><code style="font-size:12px;color:#2563EB">User ID: ${userId}</code>`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2563EB",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, save!",
        });
        if (!result.isConfirmed) return;

        try {
            setSaving(true);

            const enabledCards = referenceCards
                .filter(c => permissions[userKey]?.[c.id])
                .map(c => c.id);

            // Payload: userId + referenceCards
            const rolePositionData = { userId, referenceCards: enabledCards };

            // Find existing record for this specific user
            const existingRecord = savedAccess.find(a => {
                const parsed = parseRolePosition(a.role_Position);
                return String(parsed.userId) === userKey;
            });

            const payload = {
                id: existingRecord?.id || 0,
                role_Position: JSON.stringify(rolePositionData),
                isActive: true,
            };

            const url = existingRecord
                ? `${API_BASE_URL}api/Ref_Access_cards/${existingRecord.id}`
                : `${API_BASE_URL}api/Ref_Access_cards`;

            console.log(`[Save] ${existingRecord ? "PUT" : "POST"} — userId=${userId} — cards:`, enabledCards);

            const res = await fetch(url, {
                method: existingRecord ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) throw new Error(`${res.status} - ${await res.text()}`);

            setIsDirty(false);
            await fetchData();

            Swal.fire({
                icon: "success", title: "Saved!",
                text: `Card access updated for ${userName} (ID: ${userId}).`,
                confirmButtonColor: "#2563EB", timer: 2000, showConfirmButton: false,
            });
        } catch (err) {
            Swal.fire({ icon: "error", title: "Error", text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (selectedKey) {
            setPermissions(prev => ({
                ...prev,
                [selectedKey]: { ...(origPermissions[selectedKey] || {}) }
            }));
        }
        setIsDirty(false);
    };

    if (loading) return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#F0F7FF" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                <div style={{
                    width: 48, height: 48, border: "3px solid #BFDBFE",
                    borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.8s linear infinite"
                }} />
                <p style={{ color: "#64748B", fontSize: 14, fontWeight: 500 }}>Loading access manager...</p>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return (
        <div style={{ minHeight: "100vh", background: "#F0F7FF", fontFamily: "'Outfit', 'Segoe UI', sans-serif", color: "#1E293B" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }
                ::-webkit-scrollbar { width: 5px; }
                ::-webkit-scrollbar-track { background: #F0F7FF; }
                ::-webkit-scrollbar-thumb { background: #BFDBFE; border-radius: 99px; }
                ::-webkit-scrollbar-thumb:hover { background: #2563EB; }
                button { cursor: pointer; font-family: inherit; }
                input { font-family: inherit; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeSlideIn { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: translateX(0); } }
                @keyframes fadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
                .fade-slide { animation: fadeSlideIn 0.22s ease forwards; }
                .fade-up { animation: fadeUp 0.2s ease forwards; }
                .user-btn:hover { background: #EFF6FF !important; }
                .user-btn-active { background: #EFF6FF !important; border-color: #93C5FD !important; }
                .card-toggle:hover { background: #EFF6FF !important; border-color: #93C5FD !important; }
                .card-active { background: #EFF6FF !important; border-color: #3B82F6 !important; }
                .refresh-btn:hover { background: #DBEAFE !important; border-color: #93C5FD !important; }
                input:focus { outline: none; border-color: #3B82F6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
            `}</style>

            {/* Top bar */}
            <div style={{
                background: "#FFFFFF", borderBottom: "1px solid #DBEAFE", padding: "0 24px", height: 60,
                display: "flex", alignItems: "center", justifyContent: "space-between",
                position: "sticky", top: 0, zIndex: 20, boxShadow: "0 1px 4px rgba(37,99,235,0.07)"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: "linear-gradient(135deg, #2563EB, #38BDF8)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(37,99,235,0.25)"
                    }}>
                        <Shield size={17} color="#fff" />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: "#1E3A8A", lineHeight: 1.2 }}>
                            Reference Card Access
                        </div>
                        <div style={{ fontSize: 11.5, color: "#94A3B8", fontWeight: 400 }}>
                            Per-user access — saved by User ID
                        </div>
                    </div>
                </div>
                <button className="refresh-btn" onClick={fetchData} style={{
                    display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8,
                    background: "#F8FAFC", border: "1px solid #E2E8F0",
                    fontSize: 12, fontWeight: 500, color: "#64748B", transition: "all 0.15s"
                }}>
                    <RefreshCw size={13} /> Refresh
                </button>
            </div>

            <div style={{ display: "flex", height: "calc(100vh - 60px)" }}>

                {/* LEFT: User list */}
                <div style={{
                    width: 288, flexShrink: 0, background: "#FFFFFF",
                    borderRight: "1px solid #DBEAFE", display: "flex", flexDirection: "column",
                }}>
                    <div style={{ padding: "12px 12px 8px", borderBottom: "1px solid #EFF6FF" }}>
                        <div style={{ position: "relative" }}>
                            <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94A3B8" }} />
                            <input
                                type="text" placeholder="Search users or roles..."
                                value={userSearch} onChange={e => setUserSearch(e.target.value)}
                                style={{
                                    width: "100%", padding: "8px 30px 8px 30px",
                                    background: "#F8FAFC", border: "1px solid #E2E8F0",
                                    borderRadius: 8, fontSize: 12.5, color: "#1E293B", transition: "all 0.15s"
                                }}
                            />
                            {userSearch && (
                                <button onClick={() => setUserSearch("")} style={{
                                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                    background: "none", border: "none", color: "#94A3B8", padding: 2,
                                    display: "flex", alignItems: "center"
                                }}>
                                    <X size={12} />
                                </button>
                            )}
                        </div>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 6, paddingLeft: 2 }}>
                            {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""}
                        </div>
                    </div>

                    <div style={{ flex: 1, overflowY: "auto", padding: "8px 8px" }}>
                        {filteredUsers.length === 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 160, color: "#CBD5E1" }}>
                                <Users size={28} style={{ marginBottom: 8, opacity: 0.6 }} />
                                <p style={{ fontSize: 13 }}>No users found</p>
                            </div>
                        ) : filteredUsers.map((user) => {
                            const level = userLevelMap[user.userLevelId];
                            const levelName = level?.userLevelName || "No Role";
                            const uKey = String(user.id);
                            const cardCount = Object.values(permissions[uKey] || {}).filter(Boolean).length;
                            const isSelected = selectedUser?.id === user.id;

                            return (
                                <button
                                    key={user.id}
                                    onClick={() => handleSelectUser(user)}
                                    className={`user-btn ${isSelected ? "user-btn-active" : ""}`}
                                    style={{
                                        width: "100%", display: "flex", alignItems: "center", gap: 10,
                                        padding: "9px 10px", borderRadius: 10, border: "1px solid transparent",
                                        background: isSelected ? "#EFF6FF" : "transparent",
                                        borderColor: isSelected ? "#93C5FD" : "transparent",
                                        textAlign: "left", transition: "all 0.15s", marginBottom: 2
                                    }}
                                >
                                    <UserAvatar user={user} size={36} radius={9} fontSize={12} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1E3A8A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {user.firstName} {user.lastName}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                            <span style={{ fontSize: 11, color: "#94A3B8", fontFamily: "monospace" }}>#{user.id}</span>
                                            <span style={{ fontSize: 10, color: "#CBD5E1" }}>·</span>
                                            <span style={{ fontSize: 11.5, color: "#64748B", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {levelName}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                        <span style={{
                                            fontSize: 11, padding: "2px 7px", borderRadius: 6, fontWeight: 600,
                                            background: cardCount > 0 ? "#DBEAFE" : "#F1F5F9",
                                            color: cardCount > 0 ? "#1D4ED8" : "#94A3B8"
                                        }}>
                                            {cardCount}
                                        </span>
                                        {isSelected && <ChevronRight size={13} color="#3B82F6" />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* RIGHT: Card access panel */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#F0F7FF", overflow: "hidden" }}>
                    {!selectedUser ? (
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
                            <div style={{
                                width: 64, height: 64, borderRadius: 16, background: "#DBEAFE",
                                border: "1.5px solid #BFDBFE", display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <Users size={28} color="#3B82F6" />
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <p style={{ fontSize: 15, fontWeight: 600, color: "#1E3A8A" }}>No user selected</p>
                                <p style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>Pick a user from the left to manage their card access</p>
                            </div>
                        </div>
                    ) : (
                        <div className="fade-slide" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

                            {/* User header */}
                            <div style={{ background: "#FFFFFF", borderBottom: "1px solid #DBEAFE", padding: "16px 24px" }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                        <div style={{ boxShadow: "0 2px 8px rgba(37,99,235,0.10)", borderRadius: 12, flexShrink: 0 }}>
                                            <UserAvatar user={selectedUser} size={46} radius={12} fontSize={15} />
                                        </div>
                                        <div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, lineHeight: 1.2 }}>
                                                <span style={{ fontSize: 16, fontWeight: 700, color: "#1E3A8A" }}>
                                                    {selectedUser.firstName} {selectedUser.lastName}
                                                </span>
                                                {/* User ID badge */}
                                                <span style={{
                                                    fontSize: 11, padding: "2px 8px", borderRadius: 6,
                                                    background: "#F0FDF4", border: "1px solid #BBF7D0",
                                                    color: "#15803D", fontWeight: 700, fontFamily: "monospace"
                                                }}>
                                                    ID: {selectedUser.id}
                                                </span>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                                <span style={{ fontSize: 12, color: "#64748B" }}>{selectedUser.email}</span>
                                                <span style={{ color: "#CBD5E1" }}>·</span>
                                                <span style={{
                                                    fontSize: 11.5, padding: "2px 10px", borderRadius: 20,
                                                    background: "#DBEAFE", color: "#1D4ED8",
                                                    border: "1px solid #BFDBFE", fontWeight: 600
                                                }}>
                                                    {selectedUserLevel?.userLevelName || "No Role"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        {isDirty && (
                                            <button onClick={handleCancel} style={{
                                                padding: "7px 16px", borderRadius: 8,
                                                background: "#F8FAFC", border: "1px solid #E2E8F0",
                                                fontSize: 13, fontWeight: 500, color: "#64748B", transition: "all 0.15s"
                                            }}>
                                                Cancel
                                            </button>
                                        )}
                                        <button onClick={handleSave} disabled={saving} style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            padding: "7px 18px", borderRadius: 8,
                                            background: isDirty ? "#2563EB" : "#E2E8F0",
                                            color: isDirty ? "#FFFFFF" : "#94A3B8",
                                            border: "none", fontSize: 13, fontWeight: 600,
                                            boxShadow: isDirty ? "0 2px 8px rgba(37,99,235,0.25)" : "none",
                                            transition: "all 0.15s", cursor: isDirty ? "pointer" : "default"
                                        }}>
                                            <Save size={13} />
                                            {saving ? "Saving..." : "Save Access"}
                                        </button>
                                    </div>
                                </div>

                                {/* Per-user info banner */}
                                <div style={{
                                    marginTop: 12, display: "flex", alignItems: "center", gap: 8,
                                    padding: "8px 12px", background: "#F0FDF4",
                                    border: "1px solid #BBF7D0", borderRadius: 8
                                }}>
                                    <AlertCircle size={13} color="#15803D" style={{ flexShrink: 0 }} />
                                    <p style={{ fontSize: 12, color: "#14532D" }}>
                                        Saves only for <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> — User ID <strong>{selectedUser.id}</strong>. Other users are not affected.
                                    </p>
                                </div>
                            </div>

                            {/* Controls bar */}
                            <div style={{
                                padding: "10px 24px", background: "#FFFFFF",
                                borderBottom: "1px solid #DBEAFE",
                                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16
                            }}>
                                <div style={{ position: "relative", flex: 1, maxWidth: 280 }}>
                                    <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94A3B8" }} />
                                    <input
                                        type="text" placeholder="Search cards..." value={cardSearch}
                                        onChange={e => setCardSearch(e.target.value)}
                                        style={{
                                            width: "100%", padding: "7px 10px 7px 30px",
                                            background: "#F8FAFC", border: "1px solid #E2E8F0",
                                            borderRadius: 8, fontSize: 12.5, color: "#1E293B", transition: "all 0.15s"
                                        }}
                                    />
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <span style={{ fontSize: 12.5, color: "#64748B" }}>
                                        <span style={{ color: "#2563EB", fontWeight: 700 }}>{enabledCount}</span>
                                        <span style={{ color: "#94A3B8" }}> / {referenceCards.length} enabled</span>
                                    </span>
                                    <button onClick={() => selectAll(true)} style={{
                                        fontSize: 12, padding: "5px 12px", borderRadius: 7,
                                        background: "#DBEAFE", border: "1px solid #BFDBFE",
                                        color: "#1D4ED8", fontWeight: 600
                                    }}>All</button>
                                    <button onClick={() => selectAll(false)} style={{
                                        fontSize: 12, padding: "5px 12px", borderRadius: 7,
                                        background: "#F8FAFC", border: "1px solid #E2E8F0",
                                        color: "#64748B", fontWeight: 500
                                    }}>None</button>
                                </div>
                            </div>

                            {/* Cards grid */}
                            <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
                                {filteredCards.length === 0 ? (
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 160, color: "#CBD5E1" }}>
                                        <Database size={28} style={{ marginBottom: 8, opacity: 0.5 }} />
                                        <p style={{ fontSize: 13 }}>No cards found</p>
                                    </div>
                                ) : (
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                                        {filteredCards.map((card, idx) => {
                                            const isChecked = permissions[selectedKey]?.[card.id] || false;
                                            return (
                                                <button
                                                    key={card.id}
                                                    onClick={() => toggleCard(card.id)}
                                                    className={`fade-up card-toggle ${isChecked ? "card-active" : ""}`}
                                                    style={{
                                                        textAlign: "left", padding: "14px", borderRadius: 12,
                                                        border: isChecked ? "1.5px solid #3B82F6" : "1.5px solid #E2E8F0",
                                                        background: isChecked ? "#EFF6FF" : "#FFFFFF",
                                                        transition: "all 0.15s",
                                                        boxShadow: isChecked ? "0 2px 8px rgba(59,130,246,0.10)" : "0 1px 3px rgba(0,0,0,0.04)",
                                                        animationDelay: `${idx * 15}ms`, opacity: 0,
                                                        animationFillMode: "forwards"
                                                    }}
                                                >
                                                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
                                                        <div style={{
                                                            width: 32, height: 32, borderRadius: 8,
                                                            background: isChecked ? "#DBEAFE" : "#F1F5F9",
                                                            display: "flex", alignItems: "center", justifyContent: "center"
                                                        }}>
                                                            {isChecked ? <Unlock size={14} color="#2563EB" /> : <Lock size={14} color="#94A3B8" />}
                                                        </div>
                                                        <div style={{
                                                            width: 18, height: 18, borderRadius: 5,
                                                            background: isChecked ? "#2563EB" : "#FFFFFF",
                                                            border: isChecked ? "1.5px solid #2563EB" : "1.5px solid #CBD5E1",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            flexShrink: 0, transition: "all 0.15s"
                                                        }}>
                                                            {isChecked && (
                                                                <svg viewBox="0 0 10 8" width="10" height="10">
                                                                    <path d="M1 4l2.5 2.5L9 1" stroke="white" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: isChecked ? "#1E3A8A" : "#475569", marginBottom: 4 }}>
                                                        {card.title}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "#94A3B8", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {card.pageKey}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}